import { useQuery } from '@tanstack/react-query';

import {
  getChatAgentCapabilities,
  type ChatAgentCapabilitiesResponse,
  type ChatContextEntityType,
} from '../api/chat.client.js';

import type { Scope } from '@govard/tenant-core';

export interface UseChatAgentCapabilitiesResult {
  capabilities: ChatAgentCapabilitiesResponse['capabilities'];
  summary: ChatAgentCapabilitiesResponse['summary'];
  isLoading: boolean;
}

const EMPTY_SUMMARY: ChatAgentCapabilitiesResponse['summary'] = {
  total: 0,
  available: 0,
  readOnly: 0,
  approvalRequired: 0,
};

export function getChatAgentCapabilitiesQueryKey(
  workspaceId: string,
  userId: string,
  permissionsFingerprint: string,
  filters: { contextEntityType: ChatContextEntityType; contextEntityId?: string },
): readonly [
  string,
  string,
  string,
  string,
  { contextEntityType: ChatContextEntityType; contextEntityId?: string },
] {
  return ['chat-agent-capabilities', workspaceId, userId, permissionsFingerprint, filters] as const;
}

export function useChatAgentCapabilities(
  scope: Scope | null,
  filters: { contextEntityType: ChatContextEntityType; contextEntityId?: string },
): UseChatAgentCapabilitiesResult {
  const workspaceId = scope?.workspaceId ?? 'anonymous';
  const userId = scope?.userId ?? 'anonymous';
  const permissionsFingerprint = scope?.permissions.slice().sort().join('|') ?? 'anonymous';
  const query = useQuery({
    queryKey: getChatAgentCapabilitiesQueryKey(
      workspaceId,
      userId,
      permissionsFingerprint,
      filters,
    ),
    queryFn: () => getChatAgentCapabilities(filters),
    enabled: scope !== null,
    retry: false,
  });

  return {
    capabilities: query.data?.capabilities ?? [],
    summary: query.data?.summary ?? EMPTY_SUMMARY,
    isLoading: query.isLoading,
  };
}
